import React from 'react';
import { SiteFooter } from '@/components/landing/SiteFooter';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, Clock, User, ChevronLeft, ChevronRight, Home, Share2 } from 'lucide-react';
import { getPostBySlug, getRecentPosts } from '@/data/blogPosts';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import SEOHead from '@/components/SEOHead';
import { blogPostingSchema, breadcrumbSchema, faqPageSchema } from '@/lib/seoSchemas';

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getPostBySlug(slug);
  const recentPosts = getRecentPosts(3).filter(p => p.id !== slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <SEOHead title="Post Not Found" description="The blog post you are looking for does not exist." path={`/blog/${slug}`} noindex={true} />
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-800 mb-4">Post Not Found</h1>
          <p className="text-slate-600 mb-6">The blog post you're looking for doesn't exist.</p>
          <Link to="/blog">
            <Button className="bg-[#00247D] hover:bg-[#001A5C]">
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: url
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.id}`}
        image={post.image}
        type="article"
        article={{
          publishedAt: post.date,
          modifiedAt: post.modifiedDate || post.date,
          author: post.author,
          tags: post.keywords || [post.category],
        }}
        jsonLd={[
          blogPostingSchema({
            title: post.title,
            description: post.excerpt,
            slug: post.id,
            date: post.date,
            modifiedDate: post.modifiedDate,
            author: post.author,
            image: post.image,
            wordCount: post.wordCount,
            category: post.category,
          }),
          breadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Blog', url: '/blog' },
            { name: post.title, url: `/blog/${post.id}` },
          ]),
          ...(post.faqs && post.faqs.length > 0 ? [faqPageSchema(post.faqs)] : []),
        ]}
      />
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="/logo-transparent.png" 
                alt="Schedule Guru" 
                className="h-10"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-slate-600 hover:text-[#00247D] flex items-center gap-1">
                <Home className="w-4 h-4" />
                Home
              </Link>
              <Link to="/blog" className="text-slate-600 hover:text-[#00247D]">Blog</Link>
              <Link to="/pricing" className="text-slate-600 hover:text-[#00247D]">Pricing</Link>
            </nav>
            <Link to="/create">
              <Button className="bg-[#00247D] hover:bg-[#001A5C]">
                Get Started Free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-500">
            <Link to="/" className="hover:text-[#00247D]">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/blog" className="hover:text-[#00247D]">Blog</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-700 truncate">{post.title}</span>
          </nav>
        </div>
      </div>

      {/* Article */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Image */}
        {post.image && (
          <div className="mb-8 -mx-4 sm:-mx-6 lg:-mx-8 sm:mx-0 sm:rounded-2xl overflow-hidden bg-slate-100">
            <img
              src={post.image}
              alt={post.title}
              loading="eager"
              className="w-full h-auto aspect-[16/9] object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
        )}
        {/* Article Header */}
        <header className="mb-8">
          <Badge className="mb-4 bg-blue-100 text-[#00247D]">
            {post.category}
          </Badge>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 leading-tight">
            {post.title}
          </h1>
          <p className="text-xl text-slate-600 mb-6">
            {post.excerpt}
          </p>
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 pb-6 border-b border-slate-200">
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {post.author}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {format(parseISO(post.date), 'MMMM d, yyyy')}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {post.readTime}
            </span>
            <Button 
              variant="ghost" 
              size="sm" 
              className="ml-auto"
              onClick={handleShare}
            >
              <Share2 className="w-4 h-4 mr-1" />
              Share
            </Button>
          </div>
        </header>

        {/* Article Content */}
        <div 
          className="blog-content prose prose-lg max-w-none prose-headings:text-slate-900 prose-headings:font-bold prose-p:text-slate-600 prose-a:text-[#00247D] prose-strong:text-slate-800 prose-ul:text-slate-600 prose-ol:text-slate-600 prose-li:marker:text-[#00247D]"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* FAQs — rendered in DOM AND in JSON-LD for FAQPage rich results */}
        {post.faqs && post.faqs.length > 0 && (
          <section className="mt-12" data-testid="blog-faq-section">
            <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
              FREQUENTLY ASKED QUESTIONS
            </h2>
            <div className="space-y-4">
              {post.faqs.map((faq, idx) => (
                <details
                  key={idx}
                  className="group bg-white rounded-lg border border-slate-200 hover:border-[#00247D]/40 transition-colors overflow-hidden"
                  data-testid={`blog-faq-${idx}`}
                >
                  <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-3 text-base font-semibold text-slate-800 hover:bg-slate-50">
                    <span>{faq.question}</span>
                    <ChevronRight className="w-4 h-4 text-[#00247D] shrink-0 transition-transform group-open:rotate-90" />
                  </summary>
                  <div className="px-5 pb-4 pt-1 text-slate-600 text-sm leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* CTA Box */}
        <div className="mt-12 bg-gradient-to-r from-[#00247D] to-[#001A5C] rounded-xl p-8 text-white text-center">
          <h3 
            className="text-2xl font-bold mb-3"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            START BUILDING YOUR TOURNAMENT SCHEDULE
          </h3>
          <p className="text-blue-100 mb-6">
            Free for up to 8 teams. No credit card. Public share URL + PDF, Excel, and iCal exports included.
          </p>
          <Link to="/create">
            <Button size="lg" className="bg-white text-[#00247D] hover:bg-slate-100">
              Create Your Schedule Now
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>

        {/* Author Box */}
        <div className="mt-12 p-6 bg-white rounded-xl border border-slate-200">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-[#00247D] flex items-center justify-center text-white text-2xl font-bold">
              SG
            </div>
            <div>
              <h4 className="font-semibold text-slate-900">{post.author}</h4>
              <p className="text-sm text-slate-600">
                Helping sports organizers run better leagues and tournaments, starting with our free schedule generator.
              </p>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {recentPosts.length > 0 && (
        <section className="bg-white border-t border-slate-200 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">More Articles</h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {recentPosts.map((relatedPost) => (
                <Link key={relatedPost.id} to={`/blog/${relatedPost.id}`}>
                  <Card className="h-full border-slate-200 hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <Badge className="mb-3 bg-blue-100 text-[#00247D]">
                        {relatedPost.category}
                      </Badge>
                      <h3 className="font-bold text-slate-800 mb-2 line-clamp-2 hover:text-[#00247D]">
                        {relatedPost.title}
                      </h3>
                      <p className="text-sm text-slate-600 line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/blog">
                <Button variant="outline" className="border-[#00247D] text-[#00247D]">
                  View All Articles
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}
